import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components_dashboard/ui/button';
import { Input } from '../components_dashboard/ui/input';
import { Label } from '../components_dashboard/ui/label';
import { Mail, Lock, Eye, EyeOff, User, Loader2, Home } from 'lucide-react';
import { getDefaultAuthenticatedRoute } from '../lib/userAccess';
import { authPathForInvite, safeReturnPath } from '../features/invitations/accept-token';

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnPath = safeReturnPath(searchParams.get('next'));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const user = await register(name, email, password);
      navigate(returnPath ?? getDefaultAuthenticatedRoute(user));
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4 login-bg">
      <div className="w-full max-w-md">
        <div className="relative p-8 glass-modal rounded-2xl shadow-float animate-slide-up">
            {/* Back to homepage arrow */}
            <a
              href={process.env.REACT_APP_LANDING_BASE_URL || "/"}
              className="absolute top-4 left-4 flex items-center justify-center w-9 h-9 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors group"
              title="Back to homepage"
            >
              <Home className="w-5 h-5 text-blue-600 group-hover:text-blue-800" strokeWidth={2.5} />
            </a>
          {/* Logo */}
          <div className="mb-8 text-center">
            <a href={process.env.REACT_APP_LANDING_BASE_URL || "/"} className="inline-flex items-center gap-2 mb-2 hover:opacity-80 transition-opacity">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent">
                <span className="text-lg font-bold text-white">T</span>
              </div>
              <span className="text-2xl font-bold tracking-tight text-slate-900">TechLadder.ai</span>
            </a>
            <p className="text-sm text-slate-500">Create your account</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="font-medium text-slate-700">Full Name</Label>
              <div className="relative">
                <User className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400" />
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 h-11 border-slate-200 focus:border-accent focus:ring-accent"
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="font-medium text-slate-700">Email</Label>
              <div className="relative">
                <Mail className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 border-slate-200 focus:border-accent focus:ring-accent"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="font-medium text-slate-700">Password</Label>
              <div className="relative">
                <Lock className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11 border-slate-200 focus:border-accent focus:ring-accent"
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute -translate-y-1/2 right-3 top-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="font-medium text-slate-700">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 h-11 border-slate-200 focus:border-accent focus:ring-accent"
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            {error && (
              <div className="p-3 text-sm text-red-600 border border-red-100 rounded-lg bg-red-50" data-testid="register-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full font-medium text-white rounded-lg h-11 bg-primary hover:bg-primary-700 shadow-soft"
              disabled={loading}
              data-testid="register-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          {/* Login Link */}
          <p className="mt-6 text-sm text-center text-slate-500">
            Already have an account?{' '}
            <Link to={returnPath ? authPathForInvite('/login') : '/login'} className="font-medium text-accent hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
